import React, { useState } from "react";
import { Pill, Plus, Trash2 } from "lucide-react";
import { Tile, TextInput } from "./primitives";

const EMPTY = {
  generic: "",
  display: "",
  brands: "",
  purpose: "",
  category: "",
  ending_clue: "",
  lookalike: "",
  safety_note: "",
  reference_url: "",
  active: true,
};

export default function MedLibraryPanel({ data, onSave, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const custom = data?.custom || [];
  const total = (data?.seed_count || 0) + (data?.custom_count || 0);

  const openNew = () => {
    setForm(EMPTY);
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (m) => {
    setForm({
      ...m,
      brands: (m.brands || []).join(", "),
      lookalike: (m.lookalike || []).join(", "),
    });
    setEditing(m.slug || m.generic);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      generic: form.generic.trim(),
      display: form.display.trim() || form.generic.trim(),
      brands: form.brands
        ? form.brands.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      lookalike: form.lookalike
        ? form.lookalike.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
    const ok = await onSave(payload);
    if (ok) {
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
    }
  };

  return (
    <Tile title={`Medication library · ${total} total`} testid="tile-med-library">
      <div className="flex items-center justify-between text-xs mb-3">
        <span className="text-[#5C6B62]">
          {data?.seed_count || 0} seeded · {data?.custom_count || 0} custom
        </span>
        {!showForm && (
          <button
            data-testid="med-lib-new"
            type="button"
            onClick={openNew}
            className="inline-flex items-center gap-1 bg-[#2B5339] text-white text-[10px] px-2.5 py-1 rounded-full hover:bg-[#1C2B23]"
          >
            <Plus size={10} /> Add medication
          </button>
        )}
      </div>

      {showForm && (
        <form
          data-testid="med-lib-form"
          onSubmit={submit}
          className="mb-4 bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg p-3 space-y-2"
        >
          <TextInput testid="ml-generic" label="Generic *" required value={form.generic} onChange={(v) => setForm({ ...form, generic: v })} />
          <TextInput testid="ml-display" label="Display name" value={form.display} onChange={(v) => setForm({ ...form, display: v })} />
          <TextInput testid="ml-brands" label="Brand names (comma separated)" value={form.brands} onChange={(v) => setForm({ ...form, brands: v })} />
          <TextInput testid="ml-category" label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
          <TextInput testid="ml-purpose" label="Purpose (plain-language)" value={form.purpose} onChange={(v) => setForm({ ...form, purpose: v })} />
          <TextInput testid="ml-lookalike" label='"Do not confuse with" (comma separated)' value={form.lookalike} onChange={(v) => setForm({ ...form, lookalike: v })} />
          <TextInput testid="ml-safety" label="Safety note (one line)" value={form.safety_note} onChange={(v) => setForm({ ...form, safety_note: v })} />
          <TextInput testid="ml-ref" label="Reference URL" value={form.reference_url} onChange={(v) => setForm({ ...form, reference_url: v })} />
          <div className="flex gap-2 pt-1">
            <button
              data-testid="ml-save"
              type="submit"
              className="text-[10px] px-3 py-1 rounded-full bg-[#2B5339] text-white hover:bg-[#1C2B23]"
            >
              {editing ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditing(null);
                setForm(EMPTY);
              }}
              className="text-[10px] px-3 py-1 rounded-full bg-white border border-[#E5E2DC] text-[#5C6B62]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {custom.length === 0 ? (
        <div className="text-xs text-[#5C6B62]">
          No custom medications yet. Add one to extend the seed library.
        </div>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {custom.map((m) => (
            <div
              key={m.slug || m.generic}
              data-testid={`ml-row-${m.slug || m.generic}`}
              className="flex items-start justify-between gap-2 text-xs border-b border-[#F0EFEA] pb-1.5 last:border-0"
            >
              <div className="min-w-0">
                <div className="font-medium text-[#1C2B23] truncate flex items-center gap-1">
                  <Pill size={10} className="text-[#2B5339] flex-shrink-0" />
                  {m.display || m.generic}
                </div>
                <div className="text-[10px] text-[#5C6B62] truncate">
                  {m.category || "—"}
                  {m.brands?.length ? ` · ${m.brands.slice(0, 2).join(", ")}` : ""}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button
                  data-testid={`ml-edit-${m.slug || m.generic}`}
                  onClick={() => openEdit(m)}
                  className="text-[10px] text-[#5C6B62] hover:text-[#2B5339]"
                >
                  Edit
                </button>
                <button
                  data-testid={`ml-del-${m.slug || m.generic}`}
                  onClick={() => onDelete(m.slug, m.display || m.generic)}
                  className="text-[10px] text-[#5C6B62] hover:text-[#C26747]"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Tile>
  );
}
